/**
 * TreatmentPalette — Raycast/Linear/Spotlight-style command palette
 * for adding treatments in Chairside Mode.
 *
 * Features:
 *  • Two tabs: ⭐ Suggested (context-aware) + 🔍 All Treatments
 *  • Instant fuzzy search across the clinic's Procedure Catalog
 *  • Category filter chips + Frequently Used bucket
 *  • Keyboard nav (↑/↓ Enter Esc; Ctrl/⌘+K opens)
 *  • Multi-select with running total
 *  • Medical Safety Flags surfaced inline (from /medical-flags API)
 *  • "Create custom treatment" fallback when nothing matches
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { apiError } from "@/lib/apiError";
import { toast } from "sonner";
import {
  Search, Sparkles, X, Plus, Loader2, Command,
  Clock, ShieldAlert, ArrowUp, ArrowDown, CornerDownLeft, Check, Trash2,
} from "lucide-react";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toothInfo } from "@/components/app/ToothSVG";

const INR = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

/** Fuzzy substring match with scoring — 0 is best. */
function _score(item, q) {
  if (!q) return 0;
  const hay = `${item.name} ${item.variant || ""} ${item.category || ""} ${item.notes || ""}`.toLowerCase();
  const name = item.name.toLowerCase();
  const ql = q.toLowerCase();
  if (name.startsWith(ql)) return 0;
  if (name.includes(ql)) return 1;
  if (hay.includes(ql)) return 2;
  return 99;
}

const CATEGORY_TONE = {
  Preventive:   "border-emerald-200 text-emerald-800 bg-emerald-50",
  Restorative:  "border-blue-200 text-blue-800 bg-blue-50",
  Endodontic:   "border-purple-200 text-purple-800 bg-purple-50",
  Prosthetic:   "border-amber-200 text-amber-800 bg-amber-50",
  Surgical:     "border-red-200 text-red-800 bg-red-50",
  Cosmetic:     "border-pink-200 text-pink-800 bg-pink-50",
  Other:        "border-pine/20 text-ink-mid bg-linen",
};

const CATEGORIES = [
  { key: "all", label: "All" },
  { key: "Preventive", label: "Preventive" },
  { key: "Restorative", label: "Restorative" },
  { key: "Endodontic", label: "Endodontic" },
  { key: "Prosthetic", label: "Prosthetic" },
  { key: "Surgical", label: "Surgical" },
  { key: "Cosmetic", label: "Cosmetic" },
  { key: "Other", label: "Other" },
];

export default function TreatmentPalette({
  open, onOpenChange, patientId, tooth, selectedSurfaces = [], onApply,
}) {
  const [tab, setTab] = useState("suggested"); // suggested | all
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [catalog, setCatalog] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [selected, setSelected] = useState([]);      // treatments to apply on Save
  const [customOpen, setCustomOpen] = useState(false);
  const [flags, setFlags] = useState([]);
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [cursor, setCursor] = useState(0);
  const [applying, setApplying] = useState(false);
  const inputRef = useRef(null);

  // ---------- data loading ----------
  const loadCatalog = useCallback(async () => {
    setLoadingCatalog(true);
    try {
      const { data } = await api.get("/procedure-catalog");
      setCatalog((data || []).filter((c) => c.active !== false));
    } catch { toast.error("Could not load treatments"); }
    finally { setLoadingCatalog(false); }
  }, []);

  const loadSuggestions = useCallback(async () => {
    if (!patientId) return;
    setLoadingSuggestions(true);
    try {
      const params = tooth ? { tooth } : {};
      const { data } = await api.get(`/patients/${patientId}/treatment-suggestions`,
                                     { params });
      setSuggestions(data.suggestions || []);
    } catch { setSuggestions([]); }
    finally { setLoadingSuggestions(false); }
  }, [patientId, tooth]);

  const loadFlags = useCallback(async () => {
    if (!patientId) return;
    try {
      const { data } = await api.get(`/patients/${patientId}/medical-flags`);
      setFlags(data.flags || []);
    } catch { setFlags([]); }
  }, [patientId]);

  useEffect(() => {
    if (!open) return;
    loadCatalog();
    loadSuggestions();
    loadFlags();
    setSelected([]);
    setQ("");
    setCursor(0);
    setTab(tooth ? "suggested" : "all");
    setTimeout(() => inputRef.current?.focus(), 80);
  }, [open, loadCatalog, loadSuggestions, loadFlags, tooth]);

  // ---------- filtered results ----------
  const visible = useMemo(() => {
    if (tab === "suggested") return suggestions;
    let list = catalog;
    if (category !== "all") list = list.filter((c) => c.category === category);
    if (q) {
      list = list
        .map((c) => ({ ...c, _s: _score(c, q) }))
        .filter((c) => c._s < 99)
        .sort((a, b) => a._s - b._s);
    } else {
      list = [...list].sort((a, b) => (a.name + a.variant).localeCompare(b.name + b.variant));
    }
    return list;
  }, [tab, catalog, suggestions, q, category]);

  useEffect(() => { setCursor(0); }, [visible.length, tab]);

  // ---------- keyboard nav ----------
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "ArrowDown") { e.preventDefault(); setCursor((c) => Math.min(c + 1, visible.length - 1)); }
      else if (e.key === "ArrowUp") { e.preventDefault(); setCursor((c) => Math.max(c - 1, 0)); }
      else if (e.key === "Enter" && visible[cursor]) {
        e.preventDefault();
        toggleSelect(visible[cursor]);
      } else if (e.key === "Tab") {
        e.preventDefault();
        setTab((t) => (t === "suggested" ? "all" : "suggested"));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, visible, cursor]);

  // ---------- global Ctrl+K to open (registered by parent) ----------

  const toggleSelect = (t) => {
    setSelected((prev) => {
      const found = prev.find((x) => x.id === t.id);
      if (found) return prev.filter((x) => x.id !== t.id);
      return [...prev, { id: t.id, name: t.name, variant: t.variant,
                         cost: t.cost, duration_min: t.duration_min,
                         category: t.category }];
    });
  };

  const isSelected = (id) => selected.some((x) => x.id === id);
  const total = selected.reduce((s, x) => s + Number(x.cost || 0), 0);
  const totalDuration = selected.reduce((s, x) => s + Number(x.duration_min || 0), 0);

  const apply = async () => {
    if (selected.length === 0) return;
    setApplying(true);
    try {
      for (const t of selected) {
        const displayName = t.variant ? `${t.name} (${t.variant})` : t.name;
        await onApply({ name: displayName, cost: t.cost, duration_min: t.duration_min });
      }
      toast.success(
        `${selected.length} treatment${selected.length === 1 ? "" : "s"} planned`,
      );
      onOpenChange(false);
    } catch (e) {
      toast.error(apiError(e, "Failed to add some treatments"));
    } finally { setApplying(false); }
  };

  const clearAll = () => setSelected([]);
  const tInfo = tooth ? toothInfo(tooth) : null;
  const surfacesLabel = (selectedSurfaces || []).length
    ? selectedSurfaces.map((s) => s.toUpperCase()).join(" · ") : null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        data-testid="treatment-palette"
        overlayClassName="!bg-black/25 backdrop-blur-[2px]"
        className="!p-0 w-full sm:!max-w-[70vw] md:!max-w-[60vw] lg:!max-w-[48vw] xl:!max-w-[44vw] 2xl:!max-w-[42vw] flex flex-col bg-white/95 backdrop-blur-xl border-l border-[#E8EAE9]"
      >
        {/* ---------- Sticky header ---------- */}
        <SheetHeader className="px-5 pt-5 pb-3 border-b border-[#E8EAE9] shrink-0 space-y-2 text-left">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-ink-soft">
                {tooth ? "Plan a treatment for" : "Plan a treatment"}
              </div>
              <SheetTitle className="font-display text-2xl text-ink truncate">
                {tooth ? (
                  <span className="flex items-center gap-2 flex-wrap">
                    <span className="inline-flex w-9 h-9 rounded-lg bg-pine text-linen text-sm items-center justify-center font-mono">
                      {tooth}
                    </span>
                    <span>{tInfo?.name || `Tooth ${tooth}`}</span>
                  </span>
                ) : (
                  <span>Add treatment</span>
                )}
              </SheetTitle>
              {(tInfo?.quadrant || surfacesLabel) && (
                <div className="mt-1 text-xs text-ink-mid flex items-center gap-2 flex-wrap">
                  {tInfo?.quadrant && (
                    <Badge variant="outline" className="border-pine/20 text-ink-mid text-[10px]">
                      {tInfo.quadrant}
                    </Badge>
                  )}
                  {surfacesLabel && (
                    <span className="text-[11px] font-mono text-ink-mid">
                      Surfaces · {surfacesLabel}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </SheetHeader>

        {/* ---------- Sticky search + tabs + filters ---------- */}
        <div className="shrink-0 border-b border-[#E8EAE9]">
          <div className="px-5 pt-3 pb-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-soft" />
              <Input
                ref={inputRef}
                value={q}
                onChange={(e) => { setQ(e.target.value); setTab("all"); }}
                placeholder="Search treatments…"
                data-testid="treatment-palette-search"
                className="pl-9 pr-16 h-10 text-sm rounded-xl"
              />
              <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1 text-[10px] text-ink-soft">
                <kbd className="px-1.5 py-0.5 rounded border border-[#E8EAE9] bg-white">
                  <Command className="w-2.5 h-2.5 inline" /> K
                </kbd>
              </div>
            </div>
          </div>

          {/* Tabs + category chips */}
          <div className="flex items-center gap-1 px-4 py-2 bg-linen/40 overflow-x-auto no-scrollbar">
            <button
              type="button"
              onClick={() => setTab("suggested")}
              data-testid="treatment-palette-tab-suggested"
              className={`text-xs px-3 py-1 rounded-full flex items-center gap-1 shrink-0 transition ${
                tab === "suggested" ? "bg-pine text-linen" : "text-ink-mid hover:bg-white"
              }`}
            >
              <Sparkles className="w-3 h-3" /> Suggested
              {suggestions.length > 0 && (
                <Badge variant="outline" className={`text-[9px] ml-1 ${tab==='suggested'?'border-linen/30 text-linen':'border-pine/20 text-ink-mid'}`}>
                  {suggestions.length}
                </Badge>
              )}
            </button>
            <button
              type="button"
              onClick={() => setTab("all")}
              data-testid="treatment-palette-tab-all"
              className={`text-xs px-3 py-1 rounded-full flex items-center gap-1 shrink-0 transition ${
                tab === "all" ? "bg-pine text-linen" : "text-ink-mid hover:bg-white"
              }`}
            >
              <Search className="w-3 h-3" /> All
              <Badge variant="outline" className={`text-[9px] ml-1 ${tab==='all'?'border-linen/30 text-linen':'border-pine/20 text-ink-mid'}`}>
                {catalog.length}
              </Badge>
            </button>
            {tab === "all" && (
              <div className="ml-2 flex items-center gap-1">
                {CATEGORIES.map((c) => (
                  <button
                    key={c.key}
                    type="button"
                    onClick={() => setCategory(c.key)}
                    data-testid={`treatment-palette-cat-${c.key}`}
                    className={`text-[10px] px-2 py-1 rounded-full whitespace-nowrap shrink-0 transition ${
                      category === c.key
                        ? "bg-pine/10 text-ink border border-pine/30"
                        : "text-ink-soft hover:text-ink border border-transparent"
                    }`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Medical flags */}
          {flags.length > 0 && (
            <div className="flex items-start gap-2 px-4 py-2 bg-amber-50 border-t border-amber-200 text-[11px] text-amber-900"
                 data-testid="treatment-palette-flags">
              <ShieldAlert className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <div className="min-w-0 flex-1 flex flex-wrap gap-x-3 gap-y-0.5">
                {flags.slice(0, 3).map((f) => (
                  <span key={f.text} className="inline-flex items-center gap-1">
                    <span>⚠</span><span>{f.text}</span>
                  </span>
                ))}
                {flags.length > 3 && (
                  <span className="text-amber-700/70">+{flags.length - 3} more</span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* ---------- Only the list scrolls ---------- */}
        <div className="flex-1 min-h-0 overflow-y-auto" data-testid="treatment-palette-list">
          {(loadingCatalog || loadingSuggestions) && (
            <div className="flex items-center justify-center py-14 text-ink-soft">
              <Loader2 className="w-4 h-4 animate-spin" />
            </div>
          )}
          {!loadingCatalog && !loadingSuggestions && visible.length === 0 && (
            <div className="text-center py-14 px-4">
              <div className="text-sm text-ink-soft">
                {tab === "suggested"
                  ? "No AI suggestions yet. Try adding conditions to the tooth first, or open All Treatments."
                  : `Can't find "${q}"?`}
              </div>
              {tab === "all" && (
                <button
                  type="button"
                  onClick={() => setCustomOpen(true)}
                  data-testid="treatment-palette-custom-btn"
                  className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-pine text-linen text-xs hover:bg-pine/90"
                >
                  <Plus className="w-3.5 h-3.5" /> Create custom treatment
                </button>
              )}
            </div>
          )}
          {!loadingCatalog && !loadingSuggestions && visible.map((t, i) => {
            const active = i === cursor;
            const sel = isSelected(t.id);
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => toggleSelect(t)}
                onMouseEnter={() => setCursor(i)}
                data-testid={`treatment-palette-item-${t.id}`}
                className={`w-full text-left px-5 py-3 border-b border-[#F2F0EB] last:border-b-0 transition flex items-center gap-3 ${
                  active ? "bg-linen" : "bg-white hover:bg-linen/60"
                }`}
              >
                <div className={`w-6 h-6 rounded-md border grid place-items-center shrink-0 transition ${
                  sel ? "bg-pine border-pine" : "border-pine/20"
                }`}>
                  {sel && <Check className="w-3.5 h-3.5 text-linen" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-ink truncate">
                      {t.name}{t.variant ? ` — ${t.variant}` : ""}
                    </span>
                    {t.category && (
                      <span className={`text-[10px] px-1.5 py-0.5 rounded border ${
                        CATEGORY_TONE[t.category] || CATEGORY_TONE.Other
                      }`}>
                        {t.category}
                      </span>
                    )}
                    {t._reason && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200 flex items-center gap-1">
                        <Sparkles className="w-2.5 h-2.5" /> {t._reason}
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-ink-soft mt-0.5 flex items-center gap-3">
                    <span className="font-medium text-ink-mid">{INR(t.cost)}</span>
                    {t.duration_min ? (
                      <span className="inline-flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {t.duration_min} min
                      </span>
                    ) : null}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* ---------- Sticky selected footer ---------- */}
        <div className="shrink-0 border-t border-[#E8EAE9] bg-white">
          {selected.length > 0 && (
            <div className="px-5 py-3 border-b border-[#E8EAE9] bg-linen/50 max-h-[180px] overflow-y-auto space-y-1.5"
                 data-testid="treatment-palette-selected">
              <div className="flex items-center justify-between mb-1">
                <div className="text-[10px] uppercase tracking-widest text-ink-soft">
                  Selected treatments ({selected.length})
                </div>
                <button type="button" onClick={clearAll}
                        data-testid="treatment-palette-clear-all"
                        className="text-[10px] text-ink-soft hover:text-red-600 uppercase tracking-widest">
                  Clear all
                </button>
              </div>
              {selected.map((s) => (
                <div key={s.id} className="flex items-center gap-2 bg-white rounded-xl px-3 py-2 border border-[#EDEBE3] animate-in fade-in slide-in-from-right-1">
                  <Check className="w-3.5 h-3.5 text-ink shrink-0" />
                  <span className="text-sm text-ink flex-1 truncate">
                    {s.name}{s.variant ? ` — ${s.variant}` : ""}
                  </span>
                  <span className="text-xs text-ink-mid">{INR(s.cost)}</span>
                  {s.duration_min && <span className="text-[10px] text-ink-soft">{s.duration_min}m</span>}
                  <button type="button" onClick={() => toggleSelect(s)}
                          className="text-ink-soft hover:text-red-600"
                          data-testid={`treatment-palette-selected-remove-${s.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="px-5 py-3 flex items-center justify-between text-xs gap-3">
            <div className="flex items-center gap-3 text-ink-soft min-w-0">
              <span className="hidden sm:inline-flex items-center gap-1">
                <ArrowUp className="w-3 h-3" /><ArrowDown className="w-3 h-3" /> Navigate
              </span>
              <span className="hidden sm:inline-flex items-center gap-1">
                <CornerDownLeft className="w-3 h-3" /> Select
              </span>
              {selected.length > 0 && (
                <span className="text-ink">
                  <span className="text-[10px] uppercase tracking-widest text-ink-soft">Estimated total</span>{" "}
                  <b className="font-mono ml-1">{INR(total)}</b>
                  {totalDuration > 0 && <span className="text-ink-soft"> · {totalDuration}m</span>}
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={apply}
              disabled={selected.length === 0 || applying}
              data-testid="treatment-palette-apply"
              className="text-sm px-5 py-2 rounded-full bg-pine text-linen hover:bg-pine/90 disabled:opacity-40 inline-flex items-center gap-1.5 shrink-0"
            >
              {applying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
              Done {selected.length > 0 ? `(${selected.length})` : ""}
            </button>
          </div>
        </div>

        {/* Custom treatment sub-dialog */}
        <CustomTreatmentDialog
          open={customOpen}
          onClose={() => setCustomOpen(false)}
          seedName={q}
          onCreated={async (created) => {
            setCustomOpen(false);
            await loadCatalog();
            toggleSelect(created);
            toast.success(`"${created.name}" added to your Treatment Master`);
          }}
        />
      </SheetContent>
    </Sheet>
  );
}


// ---------- Custom Treatment sub-dialog ----------
function CustomTreatmentDialog({ open, onClose, seedName, onCreated }) {
  const [name, setName] = useState("");
  const [variant, setVariant] = useState("");
  const [category, setCategory] = useState("Other");
  const [cost, setCost] = useState(0);
  const [duration, setDuration] = useState(30);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setName(seedName || ""); setVariant(""); setCost(0); setDuration(30); setNotes(""); } }, [open, seedName]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/procedure-catalog", {
        name, variant: variant || null, category, cost: Number(cost),
        duration_min: Number(duration) || null, notes: notes || null, active: true,
      });
      onCreated(data);
    } catch (err) {
      toast.error(apiError(err, "Could not create treatment"));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md bg-white" data-testid="treatment-palette-custom-dialog">
        <div className="flex items-center justify-between mb-3">
          <div className="font-display text-xl text-ink">Custom treatment</div>
          <button onClick={onClose} className="text-ink-soft hover:text-ink"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={save} className="space-y-3">
          <Input required placeholder="Treatment name" value={name}
                 onChange={(e) => setName(e.target.value)}
                 data-testid="treatment-palette-custom-name" />
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="Variant (optional)" value={variant} onChange={(e) => setVariant(e.target.value)} />
            <select value={category} onChange={(e) => setCategory(e.target.value)}
                    className="h-10 rounded-md border border-input bg-background px-3 text-sm">
              {["Preventive","Restorative","Endodontic","Prosthetic","Surgical","Cosmetic","Other"].map((c) =>
                <option key={c}>{c}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Input required type="number" min={0} placeholder="Price ₹" value={cost}
                   onChange={(e) => setCost(e.target.value)}
                   data-testid="treatment-palette-custom-cost" />
            <Input type="number" min={5} step={5} placeholder="Duration (min)" value={duration}
                   onChange={(e) => setDuration(e.target.value)} />
          </div>
          <textarea rows={2} placeholder="Notes (optional)" value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="w-full rounded-xl border border-[#E8EAE9] px-4 py-2 text-sm resize-none" />
          <button type="submit" disabled={busy}
                  data-testid="treatment-palette-custom-save"
                  className="w-full h-10 rounded-full bg-pine text-linen text-sm inline-flex items-center justify-center gap-1.5 disabled:opacity-40">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            Save & add to Master
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
